import React, { useState, useEffect } from 'react';
import { EditPencil, PlusIcon } from '../../../public/icons';
import { MusicIcon, GalleryImageIcon, FileIcon } from '../../../public/icons';
import { useToast } from '../../../context/ToastContext';
import adminFutureGiftService from '../../../services/admin/admin-futureGiftService';
import AddGiftTypeModal from './modal/AddGiftTypeModal';
import ConfirmDialog from '../../shared/ConfirmDialog';

const GiftTypesTab = ({ refreshTrigger, onRefresh }) => {
  const { success, error } = useToast();
  const [giftTypes, setGiftTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingGiftType, setEditingGiftType] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  
  // Confirm Dialog state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  // Fetch gift types
  const fetchGiftTypes = async () => {
    try {
      setLoading(true);
      console.log('🔄 [GiftTypesTab] Fetching gift types...');
      
      const response = await adminFutureGiftService.getGiftTypes();
      
      if (response.success) {
        console.log('✅ [GiftTypesTab] Gift types fetched:', response.data);
        setGiftTypes(response.data);
      } else {
        error('Failed to fetch gift types');
      }
    } catch (err) {
      console.error('💥 [GiftTypesTab] Error fetching gift types:', err);
      error('Unable to load gift types. Please check your connection.');
    } finally {
      setLoading(false);
    }
  };

  // Load on mount and when refreshTrigger changes
  useEffect(() => {
    fetchGiftTypes();
  }, [refreshTrigger]);

  const handleAddGiftType = async (giftTypeData) => {
    try {
      setModalLoading(true);
      
      console.log('📤 [GiftTypesTab] Creating gift type:', giftTypeData);

      const response = await adminFutureGiftService.createGiftType(giftTypeData);
      
      if (response.success) {
        success('Gift type created successfully!');
        setShowAddModal(false);
        fetchGiftTypes();
        if (onRefresh) onRefresh();
      } else {
        error(response.message || 'Failed to create gift type');
      }
    } catch (err) {
      console.error('💥 [GiftTypesTab] Error creating gift type:', err);
      error('Failed to create gift type. Please try again.');
    } finally {
      setModalLoading(false);
    }
  };

  const handleEditGiftType = async (giftTypeData) => {
    try {
      setModalLoading(true);
      
      console.log('📤 [GiftTypesTab] Updating gift type:', giftTypeData);

      const response = await adminFutureGiftService.updateGiftType(editingGiftType._id, giftTypeData);
      
      if (response.success) {
        success('Gift type updated successfully!');
        setShowAddModal(false);
        setEditingGiftType(null);
        fetchGiftTypes();
        if (onRefresh) onRefresh();
      } else {
        error(response.message || 'Failed to update gift type');
      }
    } catch (err) {
      console.error('💥 [GiftTypesTab] Error updating gift type:', err);
      error('Failed to update gift type. Please try again.');
    } finally {
      setModalLoading(false);
    }
  };

  const handleEdit = (giftType) => {
    setEditingGiftType(giftType);
    setShowAddModal(true);
  };

  const showDeleteConfirmation = (giftTypeId, giftTypeName) => {
    setConfirmAction({
      title: 'Delete Gift Type',
      message: `Are you sure you want to delete "${giftTypeName}"? This action cannot be undone.`,
      confirmText: 'Delete Gift Type',
      confirmVariant: 'danger',
      onConfirm: () => handleDelete(giftTypeId)
    });
    setShowConfirmDialog(true);
  };

  const handleDelete = async (giftTypeId) => {
    try {
      setModalLoading(true);
      const response = await adminFutureGiftService.deleteGiftType(giftTypeId);
      
      if (response.success) {
        success('Gift type deleted successfully');
        fetchGiftTypes();
      } else {
        error('Failed to delete gift type');
      }
    } catch (err) {
      console.error('💥 [GiftTypesTab] Error deleting gift type:', err);
      error('Failed to delete gift type');
    } finally {
      setModalLoading(false);
      setShowConfirmDialog(false);
      setConfirmAction(null);
    }
  };

  const handleToggleStatus = async (giftTypeId, currentEnabled) => {
    try {
      const response = await adminFutureGiftService.toggleGiftType(giftTypeId, !currentEnabled);
      
      if (response.success) {
        success(`Gift type ${!currentEnabled ? 'enabled' : 'disabled'} successfully`);
        fetchGiftTypes();
      } else {
        error('Failed to toggle gift type status');
      }
    } catch (err) {
      console.error('💥 [GiftTypesTab] Error toggling gift type:', err);
      error('Failed to toggle gift type');
    }
  };

  const handleModalClose = () => {
    setShowAddModal(false);
    setEditingGiftType(null);
  };

  const getIcon = (iconType) => {
    switch (iconType) {
      case 'music':
        return <MusicIcon className="w-6 h-6 text-[#D2A146]" />;
      case 'gallery':
        return <GalleryImageIcon className="w-6 h-6 text-[#D2A146]" />;
      case 'file':
        return <FileIcon className="w-6 h-6 text-[#D2A146]" />;
      default:
        return <FileIcon className="w-6 h-6 text-[#D2A146]" />;
    }
  };

  const formatPrice = (price) => {
    if (!price) return '₦0';
    return `₦${parseInt(price).toLocaleString()}`;
  };

  if (loading && giftTypes.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#55000C]"></div>
        <span className="ml-3 text-gray-600">Loading gift types...</span>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      {/* Gift Types Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {giftTypes.map((gift) => (
          <div key={gift._id} className="bg-white rounded-sm overflow-hidden shadow-sm">
            {/* Header */}
            <div className="py-5 border-b-2 border-[#DBDBDB] mx-5">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-[#FFEDF0] p-2 rounded">
                    {getIcon(gift.icon)}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#0E1628]">{gift.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <div className={`w-2 h-2 rounded-full ${
                        gift.enabled ? 'bg-green-500' : 'bg-yellow-500'
                      }`}></div>
                      <span className="text-sm text-gray-600">
                        {gift.enabled ? 'Active' : 'Draft'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleToggleStatus(gift._id, gift.enabled)}
                    className="text-xs text-gray-600 hover:text-gray-800 px-2 py-1"
                    disabled={modalLoading}
                  >
                    {gift.enabled ? 'Disable' : 'Enable'}
                  </button>
                  <button
                    onClick={() => handleEdit(gift)}
                    className="text-[#D2A146] hover:text-[#C49660] p-2"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => showDeleteConfirmation(gift._id, gift.name)}
                    className="text-red-600 hover:text-red-800 p-2"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="p-6 space-y-4">
              <div className='border-b-2 border-[#DBDBDB] space-y-4 pb-6'>
                <div className="flex justify-between items-center">
                  <span className="text-[#8A9099] font-normal text-base">Base Price</span>
                  <span className="font-medium text-[#0E1628]">
                    {formatPrice(gift.basePrice)}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-[#8A9099] font-normal text-base">Delivery Mode</span>
                  <span className="font-medium text-[#0E1628] capitalize">
                    {gift.deliveryMode || 'Physical'}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-[#8A9099] font-normal text-base">Require content upload?</span>
                  <span className="font-medium text-[#0E1628]">
                    {gift.requireUpload ? 'Yes' : 'No'}
                  </span>
                </div>

                <div className="flex justify-between items-center">
                  <span className="text-[#8A9099] font-normal text-base">Content type</span>
                  <span className="font-medium text-[#0E1628]">{gift.contentType || 'All media'}</span>
                </div>

                {gift.leadTime && (
                  <div className="flex justify-between items-center">
                    <span className="text-[#8A9099] font-normal text-base">Lead time</span>
                    <span className="font-medium text-[#0E1628]">{gift.leadTime} days</span>
                  </div>
                )}

                {gift.deviceSelection !== undefined && (
                  <div className="flex justify-between items-center">
                    <span className="text-[#8A9099] font-normal text-base">Device selection required</span>
                    <span className="font-medium text-[#0E1628]">
                      {gift.deviceSelection ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}

                {gift.accessMethod && (
                  <div className="flex justify-between items-center">
                    <span className="text-[#8A9099] font-normal text-base">Access method</span>
                    <span className="font-medium text-[#0E1628]">{gift.accessMethod}</span>
                  </div>
                )}
              </div>

              {gift.description && (
                <div className="text-left">
                  <h4 className="text-[#8A9099] font-normal text-base mb-2">Description</h4>
                  <p className="text-base font-light text-[#0E1628]">
                    {gift.description}
                  </p>
                </div>
              )}

              {gift.internalNotes && (
                <div className="text-left">
                  <h4 className="text-[#8A9099] font-normal text-base mb-2">Internal Notes</h4>
                  <p className="text-base font-light text-[#0E1628]">
                    {gift.internalNotes}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* Add New Gift Type Card */}
        <div 
          onClick={() => {
            setEditingGiftType(null);
            setShowAddModal(true);
          }}
          className="bg-white rounded-sm border-2 border-dashed border-[#E4E4E4] hover:border-[#D2A146] cursor-pointer transition-colors shadow-sm"
        >
          <div className="flex flex-col items-center justify-center h-full min-h-[400px] p-6 text-center">
            <div className="w-12 h-12 bg-[#EEEEEE] rounded-md flex items-center justify-center mb-4">
              <PlusIcon className="w-6 h-6 text-[#7F7F7F]" />
            </div>
            <h3 className="text-base font-medium text-[#0E1628]">Create New Gift Type</h3>
            <p className="text-[#8A9099]">Define a new future gift option</p>
            <button className="mt-4 px-6 py-2 bg-[#D2A146] text-white font-medium hover:bg-[#C49660]">
              Add Gift Type
            </button>
          </div>
        </div>
      </div>

      {/* Add/Edit Gift Type Modal */}
      <AddGiftTypeModal
        isOpen={showAddModal}
        onClose={handleModalClose}
        onSubmit={editingGiftType ? handleEditGiftType : handleAddGiftType}
        loading={modalLoading}
        editingGiftType={editingGiftType}
      />

      {/* Confirm Dialog */}
      {showConfirmDialog && confirmAction && (
        <ConfirmDialog
          title={confirmAction.title}
          message={confirmAction.message}
          confirmText={confirmAction.confirmText}
          cancelText="Cancel"
          variant={confirmAction.confirmVariant}
          onConfirm={confirmAction.onConfirm}
          onCancel={() => {
            setShowConfirmDialog(false);
            setConfirmAction(null);
          }}
          isOpen={showConfirmDialog}
        />
      )}
    </div>
  );
};

export default GiftTypesTab;